import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageDetect } from '@/hooks/useLanguageDetect';
import { supabase } from '@/lib/supabase';

interface Article {
  id: string;
  title: string;
  title_zh_tw: string;
  slug: string;
  summary: string;
  summary_zh_tw: string;
  image_url: string;
  category_id: string;
  tags: string[];
  created_at: string;
}

interface Category {
  id: string;
  name: string;
  name_zh_tw: string;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function Search() {
  const { t } = useTranslation();
  const { language } = useLanguageDetect();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<Article[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, Category>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function doSearch() {
      setLoading(true);
      try {
        if (!query.trim()) {
          // No query — show all published articles
          const { data: articleData, error: articleError } = await supabase
            .from('articles')
            .select('id, title, title_zh_tw, slug, summary, summary_zh_tw, image_url, category_id, tags, created_at')
            .eq('is_published', true)
            .order('created_at', { ascending: false })
            .limit(50);

          if (articleError) throw articleError;
          setResults(articleData || []);

          const catIds = [...new Set((articleData || []).map((a) => a.category_id).filter(Boolean))];
          if (catIds.length > 0) {
            const { data: catData } = await supabase
              .from('categories')
              .select('id, name, name_zh_tw')
              .in('id', catIds);

            if (catData) {
              const map: Record<string, Category> = {};
              catData.forEach((c) => { map[c.id] = c; });
              setCategoryMap(map);
            }
          }
        } else {
          const searchTerm = `%${query}%`;

          const { data: articleData, error: articleError } = await supabase
            .from('articles')
            .select('id, title, title_zh_tw, slug, summary, summary_zh_tw, image_url, category_id, tags, created_at')
            .eq('is_published', true)
            .or(`title.ilike.${searchTerm},title_zh_tw.ilike.${searchTerm},summary.ilike.${searchTerm},summary_zh_tw.ilike.${searchTerm}`)
            .order('created_at', { ascending: false })
            .limit(30);

          if (articleError) throw articleError;
          setResults(articleData || []);

          const catIds = [...new Set((articleData || []).map((a) => a.category_id).filter(Boolean))];
          if (catIds.length > 0) {
            const { data: catData } = await supabase
              .from('categories')
              .select('id, name, name_zh_tw')
              .in('id', catIds);

            if (catData) {
              const map: Record<string, Category> = {};
              catData.forEach((c) => { map[c.id] = c; });
              setCategoryMap(map);
            }
          }
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }

    doSearch();
  }, [query]);

  return (
    <main className="w-full min-h-[60vh]">
      <section className="w-full bg-background-100 py-10 md:py-14 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            {query ? t('searchResults') : t('navHealth')}
          </h1>
          <p className="text-foreground-500 text-sm md:text-base">
            {query ? `「${query}」` : t('siteSubtitle')}
          </p>
        </div>
      </section>

      <section className="w-full py-10 md:py-14 bg-background-50 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-4 md:gap-6 bg-background-50 rounded-xl border border-background-200 p-4 md:p-5">
                  <div className="w-full sm:w-48 h-36 rounded-lg bg-background-200 animate-pulse flex-shrink-0" />
                  <div className="flex-1">
                    <div className="w-20 h-5 bg-background-200 rounded-full animate-pulse mb-2" />
                    <div className="w-full h-5 bg-background-200 rounded animate-pulse mb-2" />
                    <div className="w-3/4 h-4 bg-background-200 rounded animate-pulse mb-2" />
                    <div className="w-1/2 h-4 bg-background-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length > 0 ? (
            <div className="space-y-6">
              {results.map((article) => {
                const category = categoryMap[article.category_id];
                const displayTitle = language === 'zh-Hans' ? article.title : article.title_zh_tw;
                const displaySummary = language === 'zh-Hans' ? article.summary : article.summary_zh_tw;
                const displayCategoryName = category
                  ? (language === 'zh-Hans' ? category.name : category.name_zh_tw)
                  : '';

                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 md:gap-6 bg-background-50 rounded-xl border border-background-200 hover:border-primary-300 transition-colors p-4 md:p-5"
                  >
                    <div className="w-full sm:w-48 h-36 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        alt={displayTitle}
                        title={displayTitle}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        src={article.image_url}
                      />
                    </div>
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        {displayCategoryName && (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                            {displayCategoryName}
                          </span>
                        )}
                        <span className="text-xs text-foreground-400">{formatDate(article.created_at)}</span>
                      </div>
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors">
                        {displayTitle}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
                        {displaySummary}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-primary-600 font-medium mt-3 group-hover:text-primary-700 transition-colors">
                        {t('readMore')}
                        <i className="ri-arrow-right-line" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : query ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-foreground-400" />
              </div>
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                {t('noSearchResults')}
              </h2>
              <p className="text-sm text-foreground-500">{t('tryOtherKeywords')}</p>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                <i className="ri-article-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm text-foreground-500">暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}